import {useRef} from 'react';
import Select, {components, GroupBase, OptionProps, SingleValue, StylesConfig} from 'react-select';
import useInputValidation from '../../../hooks/input-validation-hook';
import {rootContainer, TYPE} from "../../../services/container/root";
import IsMobileService from "../../../services/is-mobile-service/is-mobile-service";
import FontBody2 from '../../fonts/font-body2/font-body2';
import FontBody3 from '../../fonts/font-body3/font-body3'
import {FONT_COLOR} from "../../fonts/font-props";
import IconInfo from '../../icons/icon-info/icon-info';
import Icon from '../../icons/icon/icon';
import './select-dropdown.scss';
import InputErrorMessages from '../input-error-messages/input-error-messages';
import { IValidationProps } from '../input/input';

export interface ISelectDropdownProps extends IValidationProps {
    options: IOption[],
    placeholder?: string,
    value?: SingleValue<IOption>,
    onChange?: (e: SingleValue<IOption>) => void;
    isLoading?: boolean;
    menuIsOpen?: boolean;
    menuWidth?: DROPDOWN_MENU_WIDTH;
    disabled?: boolean;
    styles?: StylesConfig<IOption, false>;
    isSearchable?: boolean;
    note?: string;
    target?: HTMLElement | null;
}

export enum DROPDOWN_MENU_WIDTH {
    MAX_CONTENT = 'max-content',
    FIT_CONTENT = 'fit-content',
}

export interface IOption{
    value: string | number,
    label: any,
    description?: string
    disabled?: boolean
}

const Option: React.FC<OptionProps<IOption, false, GroupBase<IOption>>> = (props) => {
    return (
        <>
            <components.Option {...props}>
                {props.data.label}
            {
                props.data.description &&
                <div>
                    <FontBody3  color={FONT_COLOR.PRIMARY3_50}>{ props.data.description}</FontBody3>
                </div>
            }
            </components.Option>
        </>
    );
};

export const SelectDropdown: React.FC<ISelectDropdownProps> = ({
    options,
    placeholder,
    value,
    onChange,
    isLoading,
    menuIsOpen,
    menuWidth = DROPDOWN_MENU_WIDTH.FIT_CONTENT,
    disabled = false,
    required,
    validationGroup,
    validationCallback,
    showValidationMessage,
    styles,
    isSearchable = true,
    note,
    /** 
     * when passed target is explicitly NULL - the drop down will be rendered inside default container 
     * when target is not passed at all - document.body will be used as the container
     * when passed target is html element - this element will be used as container
    */
    target = document.body
}) => {
    const { isValid, messages } = useInputValidation({ value : value ? value.value : '', validation: { validationGroup, required, validationCallback, showValidationMessage } });
    const ref = useRef<HTMLDivElement | null>(null);
    const isPad = rootContainer.get<IsMobileService>(TYPE.IsMobileService).isIPad();
    
    return (
        <>
        <div ref={ref} className={`select-dropdown${!isValid ? ' invalid' : ''}`}>
            <Select
                components={{ Option }}
                isDisabled={disabled}
                isOptionDisabled={(option) => !!option.disabled}
                /** Select doesn't process undefined correctly, when null is passed works well */
                value={value === undefined ? null : value}
                placeholder={placeholder}
                isSearchable={isSearchable && !isPad}
                className="select-dropdown-container"
                classNamePrefix="select-dropdown"
                options={options}
                onChange={onChange}
                isLoading={isLoading}
                menuIsOpen={menuIsOpen}
                menuPortalTarget={target}
                menuPosition="absolute"
                menuPlacement="auto"
                styles={{
                    ...styles,
                    menuPortal: base => {
                        return { ...base, zIndex: 1 }
                    },
                    menu: (base, state) => ({
                        ...base,
                        ...styles?.menu?.(base, state),
                        minWidth: ref.current?.clientWidth + 'px',
                        width: menuWidth,
                        maxWidth: '500px'
                    }),
                }}
            />
            {note &&
                <div className='note'>
                    <Icon Component={IconInfo} />
                    <FontBody2>{note}</FontBody2>
                </div>
            }
        </div>
        <InputErrorMessages messages={messages} />
        </>
)};

